import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referralService } from '../../services/referralService';
import { metricsService } from '../../services/metricsService';
import { supabase } from '../../lib/supabase';
import StatisticsCards from './components/StatisticsCards';
import FilterPanel from './components/FilterPanel';
import ReferralTable from './components/ReferralTable';
import ReferralDetailModal from './components/ReferralDetailModal';
import AISummaryModal from './components/AISummaryModal';
import SubmissionTrendsChart from './components/SubmissionTrendsChart';
import PractitionerPerformanceMetrics from './components/PractitionerPerformanceMetrics';
import ResponseTimeMetrics from './components/ResponseTimeMetrics';
import PendingCasesBreakdown from './components/PendingCasesBreakdown';
import WorkflowInsights from './components/WorkflowInsights';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';

const ReferralManagementDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showAISummaryModal, setShowAISummaryModal] = useState(false);

  // Metrics state
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [submissionTrends, setSubmissionTrends] = useState([]);
  const [practitionerPerformance, setPractitionerPerformance] = useState(null);
  const [responseTimeMetrics, setResponseTimeMetrics] = useState(null);
  const [pendingCasesAnalysis, setPendingCasesAnalysis] = useState(null);
  const [workflowInsights, setWorkflowInsights] = useState(null);

  const [filters, setFilters] = useState({
    status: 'all',
    urgency: 'all',
    referringDentist: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'submissionDate',
    direction: 'desc'
  });

  // Load data when user profile is ready
  useEffect(() => {
    if (!authLoading && userProfile) {
      loadData();
    }
  }, [authLoading, userProfile]);

  // Real-time subscription for new referrals
  useEffect(() => {
    if (!userProfile) return;

    const channel = supabase?.channel('referrals-changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'referrals'
        },
        (payload) => {
          // Convert snake_case to camelCase for new referral
          const newReferral = {
            id: payload?.new?.id,
            practitionerId: payload?.new?.practitioner_id,
            patientFirstName: payload?.new?.patient_first_name,
            patientLastName: payload?.new?.patient_last_name,
            patientYearOfBirth: payload?.new?.patient_year_of_birth,
            patientSex: payload?.new?.patient_sex,
            patientPhone: payload?.new?.patient_phone,
            patientEmail: payload?.new?.patient_email,
            referringDentistName: payload?.new?.referring_dentist_name,
            referringPracticeName: payload?.new?.referring_practice_name,
            referringDentistEmail: payload?.new?.referring_dentist_email,
            referringDentistPhone: payload?.new?.referring_dentist_phone,
            toothNumbers: payload?.new?.tooth_numbers || [],
            referralReasons: payload?.new?.referral_reasons || [],
            clinicalNotes: payload?.new?.clinical_notes,
            imagingDownloadLink: payload?.new?.imaging_download_link,
            imagingPassword: payload?.new?.imaging_password,
            imagingCenter: payload?.new?.imaging_center,
            status: payload?.new?.status,
            urgency: payload?.new?.urgency,
            submissionDate: payload?.new?.submission_date,
            reviewedAt: payload?.new?.reviewed_at,
            scheduledAt: payload?.new?.scheduled_at,
            completedAt: payload?.new?.completed_at,
            notes: payload?.new?.notes,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          };

          // Check if referral should be visible to current user
          // Super admin sees all, practitioner admin sees only their own
          const shouldShow = !userProfile?.practitionerId || 
                           newReferral?.practitionerId === userProfile?.practitionerId;

          if (shouldShow) {
            // Add new referral to the top of the list
            setReferrals(prev => [newReferral, ...prev]);
            
            // Update statistics
            setStatistics(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                totalPending: prev?.totalPending + (newReferral?.status === 'new' || newReferral?.status === 'reviewed' ? 1 : 0),
                urgentCount: prev?.urgentCount + (newReferral?.urgency === 'urgent' || newReferral?.urgency === 'emergency' ? 1 : 0),
                byStatus: {
                  ...prev?.byStatus,
                  [newReferral?.status]: (prev?.byStatus?.[newReferral?.status] || 0) + 1
                }
              };
            });
          }
        }
      )?.subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase?.removeChannel(channel);
    };
  }, [userProfile]);

  // Apply filters when data changes
  useEffect(() => {
    applyFiltersAndSort();
  }, [referrals, filters, sortConfig]);

  // Load metrics when shown
  useEffect(() => {
    if (showMetrics && userProfile) {
      loadMetrics();
    }
  }, [showMetrics, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // For super_admin without practitionerId, fetch all referrals
      // For practitioner_admin, fetch only their referrals
      const practitionerId = userProfile?.practitionerId || null;

      const [referralsData, statsData] = await Promise.all([
        referralService?.getAll(practitionerId),
        referralService?.getStatistics(practitionerId)
      ]);

      setReferrals(referralsData);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      setMetricsLoading(true);

      const practitionerId = userProfile?.practitionerId || null;

      const [trends, performance, responseTimes, pendingAnalysis, insights] = await Promise.all([
        metricsService?.getSubmissionTrends(practitionerId, 30),
        metricsService?.getPractitionerPerformance(practitionerId, 30),
        metricsService?.getResponseTimeMetrics(practitionerId, 30),
        metricsService?.getPendingCasesAnalysis(practitionerId),
        metricsService?.getWorkflowInsights(practitionerId, 30)
      ]);

      setSubmissionTrends(trends);
      setPractitionerPerformance(performance);
      setResponseTimeMetrics(responseTimes);
      setPendingCasesAnalysis(pendingAnalysis);
      setWorkflowInsights(insights);
    } catch (err) {
      console.error('Error loading metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...referrals];

    // Apply filters
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(r => r?.status === filters?.status);
    }

    if (filters?.urgency !== 'all') {
      filtered = filtered?.filter(r => r?.urgency === filters?.urgency);
    }

    if (filters?.referringDentist) {
      filtered = filtered?.filter(r =>
        r?.referringDentistName?.toLowerCase()?.includes(filters?.referringDentist?.toLowerCase())
      );
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter(r =>
        new Date(r.submissionDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(r =>
        new Date(r.submissionDate) <= new Date(filters.dateTo)
      );
    }

    if (filters?.searchTerm) {
      const searchLower = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(r =>
        r?.patientFirstName?.toLowerCase()?.includes(searchLower) ||
        r?.patientLastName?.toLowerCase()?.includes(searchLower) ||
        r?.referringDentistName?.toLowerCase()?.includes(searchLower) ||
        r?.toothNumbers?.some(tooth => tooth?.includes(filters?.searchTerm))
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.field];
      let bValue = b?.[sortConfig?.field];

      if (sortConfig?.field === 'submissionDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredReferrals(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleViewDetails = (referral) => {
    setSelectedReferral(referral);
    setShowDetailModal(true);
  };

  const handleViewAISummary = (referral) => {
    setSelectedReferral(referral);
    setShowAISummaryModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedReferral(null);
  };

  const handleStatusUpdate = async (referralId, newStatus) => {
    try {
      await referralService?.updateStatus(referralId, newStatus);
      await loadData();
      if (showMetrics) {
        await loadMetrics();
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUrgencyToggle = async (referral) => {
    try {
      // Cycle through urgency levels: routine -> urgent -> emergency -> routine
      let newUrgency;
      if (referral?.urgency === 'routine') {
        newUrgency = 'urgent';
      } else if (referral?.urgency === 'urgent') {
        newUrgency = 'emergency';
      } else {
        newUrgency = 'routine';
      }

      await referralService?.updateUrgency(referral?.id, newUrgency);
      await loadData();
      if (showMetrics) {
        await loadMetrics();
      }
    } catch (err) {
      console.error('Error updating urgency:', err);
      setError('Erreur lors de la mise à jour de l\'urgence');
    }
  };

  const handleDelete = async (referralId) => {
    try {
      await referralService?.delete(referralId);
      await loadData();
      if (showMetrics) {
        await loadMetrics();
      }
    } catch (err) {
      console.error('Error deleting referral:', err);
      setError('Erreur lors de la suppression de l\'adressage');
    }
  };

  const handleNotesUpdate = async (referralId, notes) => {
    try {
      await referralService?.updateNotes(referralId, notes);
      await loadData();
    } catch (err) {
      console.error('Error updating notes:', err);
      setError('Erreur lors de la mise à jour des notes');
    }
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      urgency: 'all',
      referringDentist: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    });
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/admin-login', { replace: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md">
          <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-4" />
          <p className="text-red-600 text-center mb-4">{error}</p>
          <button
            onClick={loadData}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col">
      <Helmet>
        <title>Gestion des Adressages - Tableau de Bord</title>
        <meta name="description" content="Interface centralisée pour gérer les adressages de patients" />
      </Helmet>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg">
                <Icon name="Activity" size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Gestion des Adressages</h1>
                <p className="text-sm text-gray-600">Tableau de bord centralisé</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/super-admin-dashboard')}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="Shield" size={18} />
                <span>Super Admin</span>
              </button>
              <button
                onClick={() => navigate('/practitioner-management-dashboard')}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="Settings" size={18} />
                <span>Mon Profil</span>
              </button>
              {userProfile && (
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{userProfile?.fullName}</p>
                  <p className="text-xs text-gray-500">
                    {userProfile?.role === 'super_admin' ? 'Super Admin' : 'Administrateur'}
                  </p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <Icon name="LogOut" size={18} />
                <span className="text-sm font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="flex-1 max-w-[1600px] mx-auto w-full px-6 py-8">
        {/* Statistics */}
        {statistics && <StatisticsCards statistics={statistics} />}

        {/* Metrics Toggle */}
        <div className="mb-8">
          <button
            onClick={() => setShowMetrics(!showMetrics)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Icon name={showMetrics ? "ChevronUp" : "BarChart"} size={20} />
            <span className="font-medium">
              {showMetrics ? 'Masquer les métriques' : 'Afficher les métriques détaillées'}
            </span>
          </button>
        </div>

        {/* Metrics Section */}
        {showMetrics && (
          <div className="space-y-8 mb-8">
            <div>
              <SubmissionTrendsChart trends={submissionTrends} loading={metricsLoading} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PendingCasesBreakdown analysis={pendingCasesAnalysis} loading={metricsLoading} />
              <WorkflowInsights insights={workflowInsights} loading={metricsLoading} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PractitionerPerformanceMetrics performance={practitionerPerformance} loading={metricsLoading} />
              <ResponseTimeMetrics metrics={responseTimeMetrics} loading={metricsLoading} />
            </div>
          </div>
        )}

        {/* Filters */}
        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Referrals Table */}
        <ReferralTable
          referrals={filteredReferrals}
          sortConfig={sortConfig}
          onSort={handleSort}
          onViewDetails={handleViewDetails}
          onViewAISummary={handleViewAISummary}
          onStatusUpdate={handleStatusUpdate}
          onDelete={handleDelete}
          onUrgencyUpdate={handleUrgencyToggle}
        />
      </main>
      {/* Detail Modal */}
      {showDetailModal && selectedReferral && (
        <ReferralDetailModal
          referral={selectedReferral}
          onClose={handleCloseDetailModal}
          onStatusUpdate={handleStatusUpdate}
          onNotesUpdate={handleNotesUpdate}
          onRefresh={loadData}
        />
      )}

      {showAISummaryModal && selectedReferral && (
        <AISummaryModal
          referral={selectedReferral}
          onClose={() => setShowAISummaryModal(false)}
          onRefresh={loadData}
        />
      )}
      <Footer />
    </div>
  );
};

export default ReferralManagementDashboard;